import React, { Fragment } from 'react';
import { PropTypes } from 'prop-types';
import { withTheme } from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import { get } from 'lodash';
import { Query } from '@apollo/client/react/components';
import {
  Fonts,
  MobileSpacer,
  PortalButton,
  Row,
  LimitedDatePicker,
  GraphAutoComplete,
  FormElements,
  Grid
} from '@custom-components';
import { themePropType } from '@themes';
import { GoogleSearch } from '@components/GooglePlacesSearch';
import { addOrRemove } from '@lib/utils';
import { fieldsPropType } from './utils';
import {
  AdvancedSearchContainer,
  CheckboxContainer,
  RadioContainer,
  ColWithBottomMargin,
  ButtonCol,
  GridContainer
} from './AdvancedSearch.styles';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';
import { LoadingSuggestion } from '../AutoComplete/Suggestions';
import { SearchChipBar } from './SearchChipBar';

const handleDateRangeChange = (values, field, handleChange) => {
  const [checkin, checkout] = values;
  if (checkin && checkout) handleChange({ target: { name: field.name, value: { checkin, checkout } } });
};

const handleExpiryDateRangeChange = (values, field, handleChange) => {
  const [startDate, endDate] = values;
  if (startDate && endDate) handleChange({ target: { name: field.name, value: { startDate, endDate } } });
};

const convertDateValue = (startDateString, endDateString) => {
  const startDateNoYear = startDateString && startDateString.split(',')[0];
  const resolveEndDate = endDateString ? ` - ${endDateString}` : '';
  const inputValue = startDateNoYear ? `${startDateNoYear}${resolveEndDate}` : '';
  return inputValue;
};

const getLabel = (conditionalLabelOn, values, label, name) =>
  conditionalLabelOn ? label(values[conditionalLabelOn]) : label || name;

// eslint-disable-next-line local-rules/no-nested-component
const buildGraphqlAutoCompleteField = (field, values, setFieldValue) => {
  const {
    name = '',
    label: inputLabel = '',
    graphql = {},
    value: inputValue,
    allowArbitraryInput,
    characterLimit,
    loading,
    updateGraphVariable,
    suggestions,
    disabled,
    error,
    errorText,
    onValueChange,
    progressiveDisclosure,
    readOnly,
    submitCount,
    styledProps = {},
    showAllSuggestions
  } = field;
  const fieldValue = values[name] ?? inputValue;

  const { conditionalQueryOn } = progressiveDisclosure ?? {};

  const gql = conditionalQueryOn ? graphql(values[conditionalQueryOn]) : graphql;

  const label = inputLabel || name;

  return (
    <GraphAutoComplete
      name={name}
      label={label}
      value={fieldValue}
      setFieldValue={setFieldValue}
      showAllSuggestions={showAllSuggestions}
      graphql={gql}
      allSearchValues={values}
      allowArbitraryInput={allowArbitraryInput}
      characterLimit={characterLimit}
      loading={loading}
      updateGraphVariable={updateGraphVariable}
      suggestions={suggestions}
      error={submitCount > 0 && error}
      errorText={errorText}
      disabled={disabled || readOnly}
      onValueChange={onValueChange}
      styledProps={styledProps}
    />
  );
};

// eslint-disable-next-line local-rules/no-nested-component
const buildGraphSelectField = (field, values, onChange) => {
  const {
    id = '',
    name = '',
    label = '',
    placeholder,
    value,
    helperText,
    onFocus,
    readOnly = false,
    graphql = {}
  } = field;
  const fieldValue = values[name] ?? value;
  const { query, mapper, accessString } = graphql;
  return (
    <Query query={query}>
      {({ data, loading }) => {
        if (loading) return <LoadingSuggestion />;
        const options = mapper(get(data, accessString, []));
        return (
          <ColWithBottomMargin md={4} sm={12}>
            <FormElements.Select
              variant="outlined"
              id={id || name}
              name={name}
              label={label}
              placeholder={placeholder}
              value={fieldValue}
              onChange={onChange}
              options={options}
              helperText={helperText}
              onFocus={onFocus}
              disabled={loading || readOnly}
            />
          </ColWithBottomMargin>
        );
      }}
    </Query>
  );
};

const buildField = (field, values, handleChange, resetBinder, setFieldValue, errors, submitCount) => {
  const {
    type,
    id = '',
    name = '',
    label: inputLabel = '',
    placeholder = '',
    options = [],
    radios = [],
    value: inputValue,
    helperText,
    onFocus,
    InputProps,
    progressiveDisclosure = {},
    readOnly = false,
    styledProps,
    enableActions,
    minCharacters,
    displayCount
  } = field;

  const { discloseOn, clearOnChange, conditionalLabelOn } = progressiveDisclosure;
  const fieldValue = values[name] ?? inputValue;
  const label = getLabel(conditionalLabelOn, values, inputLabel, name);
  let onChange = handleChange;

  if (discloseOn && !values[discloseOn]) return null;

  if (clearOnChange) {
    onChange = (e) => {
      setFieldValue(clearOnChange, '');
      handleChange(e);
    };
  }

  const handleSelectAll = (data) => {
    const filteredOptions = options?.filter(
      (option) => !data || option?.label.toLowerCase().includes(data.toLowerCase())
    );
    const multiSelectValues = [...(fieldValue || []), ...filteredOptions.map(({ value }) => value)];
    setFieldValue(name, [...new Set(multiSelectValues)]);
  };

  const handleRemoveAll = (data) => {
    if (!data.length) {
      setFieldValue(name, []);
    } else {
      const filteredOptionsValues = options
        .filter((option) => option?.label.toLowerCase().includes(data.toLowerCase()))
        .map(({ value }) => value);

      const multiSelectValues = fieldValue?.filter((value) => !filteredOptionsValues.includes(value)) ?? [];
      setFieldValue(name, [...new Set(multiSelectValues)]);
    }
  };

  const handleRemove = (val) => {
    const filteredList = val.length === 0 && fieldValue.filter((item) => item !== val);
    setFieldValue(name, filteredList);
  };

  const handleMultiSearchSelectChange = (e) => {
    setFieldValue(name, addOrRemove(fieldValue || [], e.target.value));
  };

  const commonProps = {
    variant: 'outlined',
    name,
    label,
    placeholder,
    helperText,
    onFocus,
    InputProps,
    disabled: readOnly,
    styledProps
  };

  function buildTextField(fieldType = 'text') {
    return (
      <ColWithBottomMargin md={4} sm={12}>
        <FormElements.TextInput
          {...commonProps}
          type={fieldType}
          value={fieldValue}
          onChange={onChange}
          error={!!errors[name]}
          errorText={errors[name]}
        />
      </ColWithBottomMargin>
    );
  }

  function buildSelectField(multiple = false) {
    return (
      <ColWithBottomMargin md={4} sm={12}>
        <FormElements.Select
          {...commonProps}
          id={id || name}
          value={fieldValue || (multiple ? [] : options[0]?.value)}
          onChange={onChange}
          options={options}
          multiple={multiple}
          margin={field?.margin}
        />
      </ColWithBottomMargin>
    );
  }

  function buildRadioField(radioLayout = '') {
    return (
      <ColWithBottomMargin md={radioLayout === 'horizontal' ? 8 : 4} sm={12}>
        <div>
          <Fonts.LightAccentText>{label}</Fonts.LightAccentText>
          <RadioContainer>
            <FormElements.Radios {...commonProps} activeRadio={fieldValue} radios={radios} onChange={onChange} />
          </RadioContainer>
        </div>
      </ColWithBottomMargin>
    );
  }

  function buildMultiSearchSelect() {
    return (
      <ColWithBottomMargin md={4} sm={12}>
        <FormElements.MultiSearchSelect
          {...commonProps}
          options={options}
          value={fieldValue || []}
          handleChange={handleMultiSearchSelectChange}
          selectAll={handleSelectAll}
          removeAll={handleRemoveAll}
          remove={handleRemove}
          enableActions={enableActions}
          minCharacters={minCharacters}
          displayCount={displayCount}
        />
      </ColWithBottomMargin>
    );
  }

  const content = {
    text: buildTextField('text'),
    number: buildTextField('number'),
    select: buildSelectField(),
    multiSelect: buildSelectField(true),
    multiSearchSelect: buildMultiSearchSelect(),
    radio: buildRadioField(),
    'radio-col': buildRadioField(),
    'radio-horizontal': buildRadioField('horizontal'),
    checkbox: (
      <ColWithBottomMargin>
        <CheckboxContainer>
          <FormElements.Checkbox {...commonProps} checked={!!fieldValue} onChange={onChange} />
        </CheckboxContainer>
      </ColWithBottomMargin>
    ),
    dateRange: (
      <ColWithBottomMargin md={4} sm={12}>
        <LimitedDatePicker
          isOutsideRange={() => false}
          endDate={null}
          initialStartDate={fieldValue?.checkin}
          initialEndDate={fieldValue?.checkout}
          onDatesChangeHook={(value) => handleDateRangeChange(value, field, onChange)}
        >
          {({ openPicker, resolver }) => {
            const [startDateString, endDateString] = resolver(LimitedDatePicker.PRETTY_DATE_FORMAT);
            return (
              <FormElements.TextInput
                {...commonProps}
                onFocus={openPicker}
                value={convertDateValue(startDateString, endDateString)}
                autoComplete="off"
              />
            );
          }}
        </LimitedDatePicker>
      </ColWithBottomMargin>
    ),
    expiryDateRange: (
      <ColWithBottomMargin md={4} sm={12}>
        <LimitedDatePicker
          isOutsideRange={() => false}
          endDate={null}
          initialStartDate={fieldValue?.startDate}
          initialEndDate={fieldValue?.endDate}
          onDatesChangeHook={(value) => handleExpiryDateRangeChange(value, field, onChange)}
        >
          {({ openPicker, resolver }) => {
            const [startDateString, endDateString] = resolver(LimitedDatePicker.PRETTY_DATE_FORMAT);
            return (
              <FormElements.TextInput
                {...commonProps}
                onFocus={openPicker}
                value={convertDateValue(startDateString, endDateString)}
                autoComplete="off"
              />
            );
          }}
        </LimitedDatePicker>
      </ColWithBottomMargin>
    ),
    graphAutoComplete: (
      <ColWithBottomMargin md={4} sm={12}>
        {buildGraphqlAutoCompleteField(
          { ...field, label, error: !!errors[name], errorText: errors[name], submitCount, styledProps },
          values,
          setFieldValue,
          errors
        )}
      </ColWithBottomMargin>
    ),
    graphSelect: buildGraphSelectField(
      { ...field, label, error: !!errors[name], errorText: errors[name], submitCount },
      values,
      onChange
    ),
    googleSearch: (
      <ColWithBottomMargin md={4} sm={12}>
        <GoogleSearch {...commonProps} value={fieldValue} setFieldValue={setFieldValue} />
      </ColWithBottomMargin>
    )
  }[type];

  return content ? (
    <Fragment key={name}>
      {content}
      <MobileSpacer lessThan="md" />
    </Fragment>
  ) : null;
};

export function AdvancedSearchNoTheme(props) {
  const {
    theme,
    fields,
    values,
    errors,
    isOpen,
    handleChange,
    clearSearch,
    children,
    submitSearch,
    setFieldValue,
    loading,
    transitionTime,
    withChips,
    submitCount
  } = props;

  const resetBinders = {};
  const setResetBinder = (name, reset) => {
    resetBinders[name] = reset;
  };

  const restAllFields = () => {
    clearSearch();
    Object.values(resetBinders).forEach((reset) => reset());
  };

  let chipBar;
  if (withChips && !isOpen) {
    chipBar = (
      <SearchChipBar
        theme={theme}
        fields={fields}
        values={values}
        setFieldValue={setFieldValue}
        resetBinders={resetBinders}
        submitSearch={submitSearch}
      />
    );
  }
  return (
    <>
      <CSSTransition in={isOpen} timeout={transitionTime} classNames="opensearch">
        <AdvancedSearchContainer>
          <GridContainer>
            <Row>
              {fields.map((field) =>
                buildField(field, values, handleChange, setResetBinder, setFieldValue, errors, submitCount)
              )}
              {children}
              <ButtonCol md={4} sm={12}>
                <Grid.Container nogutter style={{ display: 'flex', justifyContent: 'end' }}>
                  <PortalButton disabled={loading} onClick={() => restAllFields()} btnTheme="secondary" type="reset">
                    Clear Search
                  </PortalButton>
                  <PortalButton loading={loading} onClick={() => submitSearch()}>
                    Submit Search
                  </PortalButton>
                </Grid.Container>
              </ButtonCol>
            </Row>
          </GridContainer>
        </AdvancedSearchContainer>
      </CSSTransition>
      {chipBar}
    </>
  );
}

AdvancedSearchNoTheme.propTypes = {
  children: ReactChildrenPropType,
  clearSearch: PropTypes.func.isRequired,
  fields: fieldsPropType.isRequired,
  handleChange: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  setFieldValue: PropTypes.func,
  submitSearch: PropTypes.func.isRequired,
  theme: themePropType,
  transitionTime: PropTypes.number,
  values: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}),
  withChips: PropTypes.bool,
  submitCount: PropTypes.number
};

AdvancedSearchNoTheme.defaultProps = {
  children: undefined,
  loading: false,
  setFieldValue: () => {},
  transitionTime: 700,
  withChips: true,
  errors: {},
  submitCount: 0,
  theme: {}
};

export const AdvancedSearch = withTheme(AdvancedSearchNoTheme);
